// Content model for the in-app Help Center.
//
// Help content is plain data (see help/content/*). Pages, the search
// index, and the contextual slide-over all render from these structures,
// so a section's guide is authored once and appears everywhere. Keeping
// it as typed data (not MDX/markdown files) means it is unit-testable and
// role-aware without a build step.

import type { ComponentType } from "react";

export type Role = "viewer" | "operator" | "tenant_admin" | "super_admin";

export const roleRank: Record<Role, number> = {
  viewer: 0,
  operator: 1,
  tenant_admin: 2,
  super_admin: 3,
};

export const roleLabels: Record<Role, string> = {
  viewer: "Viewer",
  operator: "Operator",
  tenant_admin: "Company admin",
  super_admin: "Platform admin",
};

/** Whether the current role meets the minimum required role. */
export function meetsRole(current: Role | null | undefined, min: Role | undefined): boolean {
  if (!min) return true;
  return (roleRank[current ?? "viewer"] ?? 0) >= roleRank[min];
}

export interface HelpStep {
  /** The instruction. Supports **bold** and `code` inline formatting. */
  text: string;
  /** Optional tip/callout shown under the step. */
  note?: string;
}

export interface HelpTask {
  id: string;
  /** e.g. "Create a device group" — rendered as "How do I …". */
  title: string;
  /** Minimum role that can perform this action, if gated. */
  minRole?: Role;
  /** One-line lead-in shown above the steps. */
  intro?: string;
  steps: HelpStep[];
  /** What success looks like once the steps are done. */
  result?: string;
}

export interface HelpFaq {
  q: string;
  a: string;
}

export interface HelpTrouble {
  problem: string;
  /** Why it happens (optional). */
  cause?: string;
  fix: string;
}

export type RoleCell = "yes" | "no" | "partial";

export interface RoleCapability {
  capability: string;
  viewer: RoleCell;
  operator: RoleCell;
  tenant_admin: RoleCell;
  super_admin: RoleCell;
}

export type HelpGroupId = "getting-started" | "operations" | "management" | "admin";

export interface HelpArticle {
  /** URL slug, e.g. "devices" → /help/devices. */
  id: string;
  title: string;
  /** lucide-react icon component. */
  icon: ComponentType<{ size?: number | string; className?: string }>;
  group: HelpGroupId;
  /** One-line summary for the index card and search results. */
  summary: string;
  /**
   * Route path prefixes this article is the contextual help for. The
   * "?" button in the top bar resolves the current pathname to the
   * article with the longest matching prefix.
   */
  routeMatch?: string[];
  /** Hide the whole article below this role. */
  minRole?: Role;
  /** Intro paragraph(s). Supports **bold**, `code`, blank-line paragraphs and "- " bullets. */
  overview: string;
  concepts?: { term: string; definition: string }[];
  tasks?: HelpTask[];
  faqs?: HelpFaq[];
  troubleshooting?: HelpTrouble[];
  roleMatrix?: RoleCapability[];
  /** Related article ids. */
  related?: string[];
  /** Extra search keywords not already in the text. */
  keywords?: string[];
}

export interface HelpGroup {
  id: HelpGroupId;
  label: string;
  blurb: string;
}

export const helpGroups: HelpGroup[] = [
  { id: "getting-started", label: "Getting started", blurb: "Orientation, roles, and your first rollout." },
  { id: "operations", label: "Devices & rollouts", blurb: "Day-to-day fleet operations." },
  { id: "management", label: "Company & account", blurb: "Users, keys, billing, and your profile." },
  { id: "admin", label: "Platform admin", blurb: "Riot IO operators managing all companies." },
];
